import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

import { type CameraFailure, cameraPrecondition, classifyCameraError } from "../utils/cameraErrors";
import { isEmvQrPayload } from "../utils/emvQr";
import { openCamera, stopStream } from "../utils/openCamera";
import { loadQrDetector } from "../utils/qrDetector";

const SCAN_INTERVAL_MS = 160;
const FOREIGN_QR_HINT_MS = 2500;

export type ScannerStatus = "starting" | "active" | "failed";

interface TorchCapabilities extends MediaTrackCapabilities {
  torch?: boolean;
}

interface ScannerSession {
  attempt: number;
  status: ScannerStatus;
  failure: CameraFailure | null;
  torchSupported: boolean;
}

export interface QrScanner {
  status: ScannerStatus;
  failure: CameraFailure | null;
  /** A QR that is not a Bre-B code is in view — worth a hint, never a request. */
  sawForeignQr: boolean;
  torchSupported: boolean;
  torchOn: boolean;
  toggleTorch: () => void;
  retry: () => void;
}

/**
 * Rear camera into `videoRef`, scanned a few times per second until a Bre-B payload shows up. The camera is
 * released as soon as a code is read, when the tab is hidden (and taken again when it comes back) and on
 * unmount — a phone keeps its camera light on for as long as a track is alive.
 */
export function useQrScanner(
  videoRef: RefObject<HTMLVideoElement | null>,
  onDetected: (raw: string) => void,
): QrScanner {
  const [precondition] = useState(() =>
    cameraPrecondition({
      isSecureContext: window.isSecureContext,
      hasMediaDevices: typeof navigator.mediaDevices?.getUserMedia === "function",
    }),
  );
  const [attempt, setAttempt] = useState(0);
  const [session, setSession] = useState<ScannerSession | null>(null);
  const [sawForeignQr, setSawForeignQr] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const onDetectedRef = useRef(onDetected);

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  useEffect(() => {
    if (precondition) return;
    let disposed = false;
    let paused = false;
    let scanTimer: number | undefined;
    let hintTimer: number | undefined;

    const release = () => {
      window.clearTimeout(scanTimer);
      stopStream(streamRef.current);
      streamRef.current = null;
    };

    const start = async () => {
      try {
        const [detector, stream] = await openCamera(
          () =>
            navigator.mediaDevices.getUserMedia({
              // A ~480-char collection QR is ~80 modules wide; at 720p a portrait frame leaves too few pixels each.
              video: {
                facingMode: { ideal: "environment" },
                width: { ideal: 1920 },
                height: { ideal: 1080 },
              },
              audio: false,
            }),
          loadQrDetector,
        );
        const video = videoRef.current;
        if (disposed || paused || !video) {
          stopStream(stream);
          return;
        }
        streamRef.current = stream;
        video.srcObject = stream;
        await video.play().catch(() => undefined);

        const capabilities = stream.getVideoTracks()[0]?.getCapabilities?.() as TorchCapabilities | undefined;
        setSession({
          attempt,
          status: "active",
          failure: null,
          torchSupported: Boolean(capabilities?.torch),
        });

        const scan = async () => {
          if (disposed || paused) return;
          if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            try {
              const values = (await detector.detect(video)).map((code) => code.rawValue);
              const payload = values.find(isEmvQrPayload);
              if (payload && !disposed) {
                release();
                navigator.vibrate?.(60);
                onDetectedRef.current(payload);
                return;
              }
              if (values.length > 0) {
                setSawForeignQr(true);
                window.clearTimeout(hintTimer);
                hintTimer = window.setTimeout(() => setSawForeignQr(false), FOREIGN_QR_HINT_MS);
              }
            } catch {
              // A frame that cannot be read is simply retried on the next tick.
            }
          }
          scanTimer = window.setTimeout(() => void scan(), SCAN_INTERVAL_MS);
        };
        void scan();
      } catch (error) {
        if (disposed || paused) return;
        setSession({ attempt, status: "failed", failure: classifyCameraError(error), torchSupported: false });
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        paused = true;
        release();
      } else if (paused) {
        setAttempt((current) => current + 1);
      }
    };

    void start();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      disposed = true;
      window.clearTimeout(hintTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      release();
    };
  }, [attempt, precondition, videoRef]);

  const toggleTorch = useCallback(() => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const next = !torchOn;
    track
      .applyConstraints({ advanced: [{ torch: next } as MediaTrackConstraintSet] })
      .then(() => setTorchOn(next))
      .catch(() => setTorchOn(false));
  }, [torchOn]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  const current = session?.attempt === attempt ? session : null;
  return {
    status: precondition ? "failed" : (current?.status ?? "starting"),
    failure: precondition ?? current?.failure ?? null,
    sawForeignQr,
    torchSupported: current?.torchSupported ?? false,
    torchOn,
    toggleTorch,
    retry,
  };
}
